import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import type {Employee} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {Link} from 'components/DSYS/Link';

import {PinToggleButton} from './PinToggleButton';
import {useHoverReveal} from './useHoverReveal';
import {logWorkstreamsInteraction} from './workstreamsAnalytics';

// The header's footprint while the person's profile loads: avatar, name, role.
export const PersonScopeHeaderSkeleton = () => (
  <Box display="flex" alignItems="center" style={{gap: 12}} aria-busy="true">
    <Skeleton.Avatar size="large" />
    <Stack gap="4">
      <Skeleton.Text size="large" width={160} />
      <Skeleton.Text size="small" width={220} />
    </Stack>
  </Box>
);

/**
 * /workstreams: whose workstreams a person scope (`?person=`) shows,
 * above its first section. The name opens their profile in a new tab; its pin
 * (their recent's) sits right after it, like a workstream page's title, shown
 * on hover or always once pinned.
 */
export const PersonScopeHeader = ({
  person,
  pinned = false,
  onTogglePin,
}: {
  person: Employee;
  pinned?: boolean;
  onTogglePin?: () => void;
}) => {
  const pinReveal = useHoverReveal();
  return (
    <Box {...pinReveal.regionProps} display="flex" alignItems="center" style={{gap: 12}}>
      <Avatar size="large" user={person} />
      {/* Each line its own block: Stack doesn't break inline children. */}
      <Stack gap="0" style={{minWidth: 0}}>
        <Box display="flex" alignItems="center" style={{gap: 4}}>
          <Link
            to={`/people/${person.ldap}`}
            monochromatic
            hasNoUnderline
            showUnderlineOnHover
            openInNewTab
            onClick={() => logWorkstreamsInteraction('person_profile_opened')}
          >
            <Text size="large" isBold>
              {person.name}
            </Text>
          </Link>
          {onTogglePin && (
            <Box style={pinned ? undefined : pinReveal.revealStyle}>
              <PinToggleButton pinned={pinned} onToggle={onTogglePin} target="sidebar" />
            </Box>
          )}
        </Box>
        {person.role && (
          <Box>
            <Text size="small" color="subtle">
              {person.role}
            </Text>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
